/**
 * Projects List Page
 *
 * Displays all projects the user has access to with request summaries.
 * Uses TanStack Query hooks backed by the API server.
 */

import {
  Card,
  EmptyState,
  Grid,
  PageHeader,
  ProjectCard,
  type ProjectStatus,
  type RequestSummary,
  Stack,
  Text,
} from '@cortex/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { ProjectCardSkeleton } from '@/components/skeletons';
import { useDashboardProjects } from '@/hooks/use-dashboard-queries';

export const Route = createFileRoute('/_app/_main/projects/')({
  component: ProjectsPage,
});

const DEFAULT_PROJECT_STATUS: ProjectStatus = 'active';

const EMPTY_SUMMARY: RequestSummary = {
  draft: 0,
  pending: 0,
  inProgress: 0,
  review: 0,
  completed: 0,
};

function ProjectsPage() {
  const navigate = useNavigate();

  // Fetch projects and request counts via API
  const { data, isPending, error } = useDashboardProjects();

  const handleProjectClick = (projectSlug: string) => {
    navigate({ to: '/projects/$slug', params: { slug: projectSlug } });
  };

  return (
    <Stack gap="lg" className="p-6">
      {/* Header */}
      <PageHeader title="Projects" subtitle="All your projects in one place" />

      {/* Loading State */}
      {isPending && (
        <Grid gap="lg" className="sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <ProjectCardSkeleton key={i} />
          ))}
        </Grid>
      )}

      {/* Error State */}
      {error && (
        <Card className="p-4">
          <Text color="error" size="sm">
            Failed to load projects: {error.message}
          </Text>
        </Card>
      )}

      {/* Empty State */}
      {!isPending && !error && data?.projects.length === 0 && (
        <EmptyState
          type="no-data"
          title="No projects yet"
          description="Projects will appear here once they're created."
        />
      )}

      {/* Projects Grid */}
      {!isPending && !error && data && data.projects.length > 0 && (
        <Grid gap="lg" className="sm:grid-cols-2 lg:grid-cols-3">
          {data.projects.map((project) => (
            <ProjectCard
              key={project.id}
              name={project.name}
              {...(project.description != null && {
                description: project.description,
              })}
              status={DEFAULT_PROJECT_STATUS}
              requestSummary={data.summaries[project.id] ?? EMPTY_SUMMARY}
              lastUpdated={new Date(project.updated_at)}
              onCardClick={() => handleProjectClick(project.slug)}
            />
          ))}
        </Grid>
      )}
    </Stack>
  );
}
